import { Controller } from "@hotwired/stimulus"

// Adjust-style date range picker (LOL-253) — see app/views/admin/shared/_date_range_picker.
//
// The server resolves every preset (data-from / data-to on the preset buttons) and the current
// selection (values); this controller only draws two month grids, tracks a draft selection and
// writes the hidden range/from/to inputs before the GET form submits into the main-inner Turbo
// frame. Open/close is the shared `dropdown` controller. All date math is on ISO strings in
// UTC so the browser's zone can never shift a day.
//
// Grids are rebuilt only on month navigation and preset choice; picking a day repaints classes
// in place, because rebuilding would detach the clicked cell and the dropdown's window-click
// handler would then treat the click as "outside" and close the popover.
const DAY_MS = 86_400_000
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]

export default class extends Controller {
  static targets = ["keyInput", "fromInput", "toInput", "preset", "grid", "monthLabel", "summary", "apply"]
  static values = {
    anchor: String,          // last selectable day (ISO)
    today: String,           // ISO, in the admin's zone
    key: String,             // current selection
    from: String,
    to: String,
    label: String
  }

  connect() { this.reset() }

  // Back to the applied selection (open, Cancel, Escape).
  reset() {
    this.draft = { key: this.keyValue, from: this.fromValue || null, to: this.toValue || null, label: this.labelValue }
    this.picking = false
    this.hover = null
    this.viewMonth = addMonths(startOfMonth(this.draft.to || this.anchorValue), -1)
    this.renderMonths()
    this.paint()
  }

  choosePreset(event) {
    const { key, from, to, label } = event.currentTarget.dataset
    this.draft = { key, from: from || null, to: to || null, label }
    this.picking = false
    this.hover = null
    if (to) this.viewMonth = addMonths(startOfMonth(to), -1)
    this.renderMonths()
    this.paint()
  }

  pickDay(event) {
    const cell = event.target.closest("[data-date]")
    if (!cell || cell.disabled) return
    const date = cell.dataset.date
    if (this.picking) {
      let from = this.draft.from
      let to = date
      if (to < from) [from, to] = [to, from]
      this.draft = { key: "custom", from, to, label: null }
      this.picking = false
      this.hover = null
    } else {
      this.draft = { key: "custom", from: date, to: null, label: null }
      this.picking = true
    }
    this.paint()
  }

  hoverDay(event) {
    if (!this.picking) return
    const cell = event.target.closest("[data-date]")
    const date = cell && !cell.disabled ? cell.dataset.date : null
    if (date !== this.hover) {
      this.hover = date
      this.paint()
    }
  }

  clearHover() {
    if (!this.hover) return
    this.hover = null
    this.paint()
  }

  prevMonth() { this.shiftMonths(-1) }
  nextMonth() { this.shiftMonths(1) }

  shiftMonths(delta) {
    this.viewMonth = addMonths(this.viewMonth, delta)
    this.renderMonths()
    this.paint()
  }

  beforeSubmit(event) {
    const custom = this.draft.key === "custom"
    if (custom && !this.draft.to) {
      event.preventDefault()
      return
    }
    this.keyInputTarget.value = this.draft.key
    // Presets travel as ?range=7d alone; disabled inputs are left out of the query string.
    this.fromInputTarget.disabled = !custom
    this.toInputTarget.disabled = !custom
    if (custom) {
      this.fromInputTarget.value = this.draft.from
      this.toInputTarget.value = this.draft.to
    }
  }

  // --- rendering -------------------------------------------------------------------------

  renderMonths() {
    this.gridTargets.forEach((grid, i) => {
      const month = addMonths(this.viewMonth, i)
      this.monthLabelTargets[i].textContent = monthLabel(month)
      grid.innerHTML = this.monthHtml(month)
    })
  }

  monthHtml(monthIso) {
    const [year, month] = monthIso.split("-").map(Number)
    const first = Date.UTC(year, month - 1, 1)
    const offset = (new Date(first).getUTCDay() + 6) % 7 // Monday-first
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
    const cells = WEEKDAYS.map((d) => `<div class="date-range-weekday">${d}</div>`)
    for (let i = 0; i < offset; i++) cells.push('<div class="date-range-day is-outside"></div>')
    for (let day = 1; day <= daysInMonth; day++) {
      const iso = toISO(first + (day - 1) * DAY_MS)
      const disabled = iso > this.anchorValue ? " disabled" : ""
      cells.push(`<button type="button" class="date-range-day" data-date="${iso}"${disabled}>${day}</button>`)
    }
    return cells.join("")
  }

  paint() {
    const { from, to } = this.draft
    let previewFrom = null
    let previewTo = null
    if (this.picking && this.hover) {
      ;[previewFrom, previewTo] = this.hover >= from ? [from, this.hover] : [this.hover, from]
    }
    this.element.querySelectorAll("[data-date]").forEach((cell) => {
      const d = cell.dataset.date
      cell.classList.toggle("is-start", d === from || d === previewFrom)
      cell.classList.toggle("is-end", (to !== null && d === to) || (previewTo !== null && d === previewTo))
      cell.classList.toggle("in-range", from !== null && to !== null && d > from && d < to)
      cell.classList.toggle("is-preview", previewFrom !== null && d > previewFrom && d < previewTo)
      cell.classList.toggle("is-today", d === this.todayValue)
    })
    this.presetTargets.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.key === this.draft.key))
    })
    if (this.hasApplyTarget) this.applyTarget.disabled = this.picking
    if (this.hasSummaryTarget) this.summaryTarget.textContent = this.summaryText()
  }

  summaryText() {
    const { key, from, to, label } = this.draft
    if (key === "all") return "All time"
    if (key !== "custom") return `${label} · ${span(from, to)}`
    if (!to) return `${longDate(from)} → …`
    return `${longDate(from)} → ${longDate(to)}`
  }
}

// --- ISO date helpers (UTC) ----------------------------------------------------------------

function toISO(ms) {
  return new Date(ms).toISOString().slice(0, 10)
}

function startOfMonth(iso) {
  return `${iso.slice(0, 7)}-01`
}

function addMonths(iso, delta) {
  const [year, month] = iso.split("-").map(Number)
  return toISO(Date.UTC(year, month - 1 + delta, 1))
}

function utcDate(iso) {
  const [year, month, day] = iso.split("-").map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

// Hand-rolled formats so the text matches Ruby's strftime (%d %b %Y / %d %B %Y) exactly;
// Intl's en-GB says "Sept" where Ruby says "Sep".
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const parts = (iso) => {
  const [year, month, day] = iso.split("-")
  return { day, month: MONTHS[Number(month) - 1], year }
}
function monthLabel(iso) { const p = parts(iso); return `${p.month} ${p.year}` }
function longDate(iso) { const p = parts(iso); return `${p.day} ${p.month} ${p.year}` }
const shortMonth = (p) => p.month.slice(0, 3)

// "06 Sep – 12 Sep 2026" or "13 Sep 2025 – 12 Sep 2026" — mirrors Admin::DateRange#span.
function span(from, to) {
  const f = parts(from)
  const t = parts(to)
  const left = f.year === t.year ? `${f.day} ${shortMonth(f)}` : `${f.day} ${shortMonth(f)} ${f.year}`
  return `${left} – ${t.day} ${shortMonth(t)} ${t.year}`
}
